import { Box, Button, styled, TextField, Typography } from "@material-ui/core";
import { useState } from "react";
import {
  EPOCUS_BORDER,
  EPOCUS_PRIMARY,
  EPOCUS_TEXT_PRIMARY,
  EPOCUS_TEXT_SECONDARY,
  EPOCUS_WHITE,
  rem,
} from "../../../../../_lib/epocus-palette";
import { WorksheetSection } from "../../../../../_lib/worksheets/worksheet-primitives";
import type { AmendmentKind } from "./report-amendment";

const Hint = styled(Typography)({
  fontSize: rem(13),
  color: EPOCUS_TEXT_SECONDARY,
});

const buttonStyle = {
  flex: 1,
  minHeight: 48,
  textTransform: "none",
  fontWeight: 600,
  fontSize: rem(14),
} as const;

const COPY: Record<
  AmendmentKind,
  { hint: string; field: string; submit: string }
> = {
  AMEND: {
    hint: "Edit the worksheet above, then say why. Signing the amendment keeps the original report viewable.",
    field: "Reason for the amendment",
    submit: "Sign amendment",
  },
  CONFIRM: {
    hint: "The reviewer recorded a major discrepancy. Re-sign the report unchanged, with a note, to release it for billing.",
    field: "Note",
    submit: "Confirm and re-sign",
  },
};

export const AmendReportPanel: FC<{
  mode: AmendmentKind | null;
  canConfirm: boolean;
  submitting: boolean;
  onStart: (mode: AmendmentKind) => void;
  onCancel: () => void;
  onSubmit: (reason: string) => void;
}> = ({ mode, canConfirm, submitting, onStart, onCancel, onSubmit }) => {
  const [reason, setReason] = useState("");

  if (mode === null) {
    return (
      <WorksheetSection label="Signed report">
        <Hint>
          Only you can change this report. An amendment needs a reason and
          re-signs it, and the original stays viewable.
        </Hint>
        <Box display="flex" style={{ gap: 12, marginTop: 12 }}>
          <Button
            variant="outlined"
            onClick={() => onStart("AMEND")}
            style={{
              ...buttonStyle,
              borderColor: EPOCUS_BORDER,
              color: EPOCUS_TEXT_PRIMARY,
            }}
          >
            Amend report
          </Button>
          {canConfirm && (
            <Button
              variant="contained"
              disableElevation
              onClick={() => onStart("CONFIRM")}
              style={{
                ...buttonStyle,
                color: EPOCUS_WHITE,
                backgroundColor: EPOCUS_PRIMARY,
              }}
            >
              Confirm as it stands
            </Button>
          )}
        </Box>
      </WorksheetSection>
    );
  }

  const copy = COPY[mode];
  const blank = reason.trim().length === 0;
  const cancel = () => {
    setReason("");
    onCancel();
  };

  return (
    <WorksheetSection
      label={mode === "AMEND" ? "Amend report" : "Confirm report"}
    >
      <Hint>{copy.hint}</Hint>
      <TextField
        id="amend-report-reason"
        label={copy.field}
        required
        multiline
        minRows={2}
        variant="outlined"
        fullWidth
        value={reason}
        onChange={(event) => setReason(event.target.value)}
        disabled={submitting}
        style={{ marginTop: 12 }}
      />
      <Box display="flex" style={{ gap: 12, marginTop: 12 }}>
        <Button
          variant="outlined"
          onClick={cancel}
          disabled={submitting}
          style={{
            ...buttonStyle,
            borderColor: EPOCUS_BORDER,
            color: EPOCUS_TEXT_PRIMARY,
          }}
        >
          Cancel
        </Button>
        <Button
          variant="contained"
          disableElevation
          onClick={() => onSubmit(reason)}
          disabled={blank || submitting}
          style={{
            ...buttonStyle,
            fontWeight: 700,
            color: EPOCUS_WHITE,
            backgroundColor: EPOCUS_PRIMARY,
            opacity: blank || submitting ? 0.4 : 1,
          }}
        >
          {copy.submit}
        </Button>
      </Box>
    </WorksheetSection>
  );
};
